import * as tus from "tus-js-client";
import { DetailedError, type Upload } from "tus-js-client";
import { action, computed, makeObservable, observable } from "mobx";
import { newShortGuid } from "../util/types/guid";
import { DicomFileReader } from "./DicomFileReader";
import { DicomTags } from "../network/contracts/DicomDataSet";
import { DicomUID } from "../network/contracts/DicomUID";
import { DicomUploadTusFingerprintStore } from "./DicomUploadTusFingerprintStore";

export class FileUpload {
    @observable
    fileGuid: string;

    @observable
    fileIsADicomFile = false;

    @observable
    fileIsADicomDirFile = false;

    @observable
    checking = true;

    @observable
    status: UploadStatus = UploadStatus.NotStarted;

    @observable
    progress = 0;

    @observable
    bytesUploaded = 0;

    file: File;
    name: string;
    type: string;
    size: number;

    sourceId = 0;
    uploadCode: string | null = null;
    emailAddress = "";
    mobilePhone = "";

    private _upload: Upload | null = null; //Will remain null when file is not uploadable

    private readonly _endpoint: string;
    private readonly _maxUploadSize: number;
    private readonly _dicomFileReader: DicomFileReader;

    constructor(file: File, endpoint: string, maxUploadSize: number, sourceId: number, uploadCode: string | null) {
        makeObservable(this);

        this.fileGuid = newShortGuid();

        this.file = file;
        this.name = file.name;
        this.type = file.type;
        this.size = file.size;

        this._endpoint = endpoint;
        this._maxUploadSize = maxUploadSize;
        this._dicomFileReader = new DicomFileReader(file);

        this.sourceId = sourceId;
        this.uploadCode = uploadCode;
    }

    @computed
    public get fileSizeIsOk(): boolean {
        return this.size <= this._maxUploadSize;
    }

    @computed
    public get fileNameLengthIsOk(): boolean {
        return this.name.length < 255;
    }

    public getDicomTagValue(dicomTag: DicomTags): string {
        if (!this.fileIsADicomFile) return "";
        return this._dicomFileReader.getDicomTag(dicomTag)?.value ?? "";
    }

    @computed
    public get uploadable(): boolean {
        return !this.checking && this.fileSizeIsOk && this.fileIsADicomFile && !this.fileIsADicomDirFile && this.fileNameLengthIsOk;
    }

    @action
    private setStatus(status: UploadStatus) {
        this.status = status;
    }

    @action
    private setProgress(progress: number) {
        this.progress = progress;
    }

    @action
    private setBytesUploaded(bytes: number): void {
        this.bytesUploaded = bytes;
    }

    @action
    startUpload(patientGuid: string): void {
        if (!this.uploadable) return;

        if (!this._upload) {
            this.makeTusUpload(patientGuid);
        }
        if (!this._upload) return; //Should never happen since we make it right before this, but just in case...
        if (this.status === UploadStatus.Done || this.status == UploadStatus.ValidatingFiles) return; //Already done, don't trigger again or it will send the file again
        this.setStatus(UploadStatus.Started);
        this._upload.findPreviousUploads().then((previousUploads) => {
            if (previousUploads.length !== 0) {
                // most recent upload is always at index 0
                this._upload!.resumeFromPreviousUpload(previousUploads[0]);
            }
            this._upload!.start();
        });
    }

    public pauseUpload(): void {
        if (!this._upload) return;
        this._upload.abort().then((r) => this.setStatus(UploadStatus.Paused));
    }

    @action
    public cancelUpload(): void {
        if (this._upload != null && this.status === UploadStatus.Started) {
            this._upload.abort();
        }
        this._upload = null;
        this.setStatus(UploadStatus.Cancelled);
    }

    @action
    queueUpload(): void {
        if (!this.uploadable) return;
        this.setStatus(UploadStatus.Queued);
    }

    public async checkAndMakeFileUploadReady(): Promise<void> {
        await this.readFileAndDoChecks();
    }

    @computed
    public get patientId(): string {
        return this.getDicomTagValue(DicomTags.PatientID);
    }

    @computed
    public get studyId(): string {
        if (!this.fileIsADicomFile) return "";
        return this.getDicomTagValue(DicomTags.StudyInstanceUID);
    }

    @action
    private async readFileAndDoChecks(): Promise<void> {
        this.setChecking(true);

        await this._dicomFileReader.readFile();
        this.setFileIsADicomFile(this._dicomFileReader.dataSet !== null);

        const fileIsADicomDir = this.getDicomTagValue(DicomTags.MediaStorageSOPClassUID) === DicomUID.MediaStorageDirectoryStorage;
        this.setFileIsADicomDirFile(fileIsADicomDir);

        this.setChecking(false);
    }

    @action
    public setChecking(value: boolean) {
        this.checking = value;
    }

    @action
    private setFileIsADicomFile(value: boolean) {
        this.fileIsADicomFile = value;
    }

    @action
    private setFileIsADicomDirFile(value: boolean) {
        this.fileIsADicomDirFile = value;
    }

    private makeTusUpload(patientGuid: string) {
        this._upload = new tus.Upload(this.file, {
            endpoint: this._endpoint,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            storeFingerprintForResuming: true, // stores information about uploads in local storage and is needed for resuming
            removeFingerprintOnSuccess: false, // we'll clear the fingerprints manually, once the complete study is uploaded
            metadata: {
                sourceId: this.sourceId.toString(), //Add specific source id,
                uploadCode: this.uploadCode ?? "",
                emailAddress: this.emailAddress,
                mobilePhone: this.mobilePhone,
                patientGuid: patientGuid,
            },
            onError: this.handleUploadError,
            onProgress: this.handleUploadProgress, // Progress means that bytes are sent from the client and does not guarantee that it has arrived at the server
            onChunkComplete: this.handleChunkComplete, // Chunk Complete means that the package successfully arrived at the server
            onSuccess: this.handleUploadSuccess,
            uploadDataDuringCreation: false, // false needed for resumable uploads
            fingerprint: DicomUploadTusFingerprintStore.generate,
        });
    }

    private readonly handleUploadError = (error: Error | DetailedError) => {
        if (error instanceof DetailedError) {
            switch (error.originalResponse?.getBody()) {
                case "RateLimited":
                    this.setStatus(UploadStatus.RateLimited);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                case "IpBlocked":
                    this.setStatus(UploadStatus.IpBlocked);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                case "UploadCodeExpired":
                    this.setStatus(UploadStatus.UploadCodeExpired);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                case "ExceededUploadSizeLimit":
                    this.setStatus(UploadStatus.ExceededUploadSizeLimit);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                default:
                    this.setStatus(UploadStatus.Failed);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
            }
        }

        console.error("Upload failed because: ", error);
    };

    @action
    private readonly handleUploadProgress = (bytesUploaded: number, bytesTotal: number) => {
        const percentage = (bytesUploaded / bytesTotal) * 100;
        this.setBytesUploaded(bytesUploaded);
        this.setStatus(UploadStatus.Started);
        this.setProgress(percentage);
    };

    private readonly handleUploadSuccess = () => {
        this.setBytesUploaded(this.size);
        this.setStatus(UploadStatus.ValidatingFiles);
        this.setProgress(100);
    };

    private readonly handleChunkComplete = (bytesUploaded: number, bytesTotal: number) => {
        const percentage = (bytesUploaded / bytesTotal) * 100;
        this.setBytesUploaded(bytesUploaded);
        this.setStatus(UploadStatus.Started);
        this.setProgress(percentage);
    };

    @computed
    public get removable(): boolean {
        if (!this.uploadable || this.status !== UploadStatus.NotStarted) return false;
        return true;
    }

    public get uploadFingerprint(): string {
        const { _upload } = this;
        if (_upload == null) return "";

        const { url } = _upload;

        if (url == null) return "";

        return url.substring(url.lastIndexOf("/") + 1);
    }

    public maliciousContentFound(): void {
        this.setStatus(UploadStatus.MaliciousContent);
    }

    public notADicomFile(): void {
        this.setStatus(UploadStatus.NotADicomFile);
    }

    public uploadComplete(): void {
        this.setStatus(UploadStatus.Done);
    }

    public uploadFailed(): void {
        this.setStatus(UploadStatus.Failed);
    }
    public uploadExceededUploadSizeLimit(): void {
        this.setStatus(UploadStatus.ExceededUploadSizeLimit);
    }

    get arrivedAtTheBackend(): boolean {
        return (
            this.status === UploadStatus.Done ||
            this.status === UploadStatus.ValidatingFiles ||
            this.status === UploadStatus.MaliciousContent ||
            this.status === UploadStatus.NotADicomFile
        );
    }
}

export enum UploadStatus {
    NotStarted = 0,
    Queued = 1,
    Started = 2,
    Paused = 3,
    Done = 4,
    Failed = 5,
    RateLimited = 6,
    Cancelled = 7,
    ValidatingFiles = 8,
    MaliciousContent = 9,
    NotADicomFile = 10,
    IpBlocked = 11,
    UploadCodeExpired = 12,
    ExceededUploadSizeLimit = 13,
}
